<template>
  <div class="animated-background" :class="computedTheme">
    <div class="gradient-bg"></div>
    <div class="particles-container">
      <div v-for="n in particleCount" :key="n" :class="`particle particle-${n}`"></div>
    </div>
  </div>
</template>

<script>
import { useThemeStore } from '@/stores/theme';

export default {
  name: 'AnimatedBackground',
  props: {
    theme: {
      type: String,
      default: 'auto',
      validator: (value) => ['auto', 'default', 'medical', 'admin'].includes(value)
    },
    variant: {
      type: String,
      default: ''
    },
    particleCount: {
      type: Number,
      default: 30
    }
  },
  computed: {
    computedTheme() {
      // Admin variant takes priority
      if (this.variant === 'admin') {
        return 'theme-admin';
      }
      
      // If theme is explicitly set (not auto), use it
      if (this.theme && this.theme !== 'auto') {
        return `theme-${this.theme}`;
      }
      
      // Use theme store for auto-detection (syncs with navbar)
      const themeStore = useThemeStore();
      return `theme-${themeStore.activeTheme}`;
    }
  }
};
</script>

<style scoped>
.animated-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}

.gradient-bg {
  position: absolute;
  width: 100%;
  height: 100%;
  background-size: 400% 400%;
  animation: gradientFlow 20s ease infinite;
}

/* Theme Default (Green) */
.theme-default .gradient-bg {
  background: linear-gradient(
    135deg,
    rgba(240, 255, 240, 1) 0%,
    rgba(230, 250, 230, 1) 20%,
    rgba(220, 245, 225, 1) 40%,
    rgba(235, 252, 235, 1) 60%,
    rgba(225, 248, 230, 1) 80%,
    rgba(240, 255, 240, 1) 100%
  );
}

/* Theme Medical (Blue) */
.theme-medical .gradient-bg {
  background: linear-gradient(
    135deg,
    rgba(240, 248, 255, 1) 0%,
    rgba(230, 245, 255, 1) 20%,
    rgba(220, 240, 250, 1) 40%,
    rgba(235, 248, 255, 1) 60%,
    rgba(225, 242, 252, 1) 80%,
    rgba(240, 248, 255, 1) 100%
  );
}

@keyframes gradientFlow {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

/* Particles Container */
.particles-container {
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.particle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.6);
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
  animation: particleFloat linear infinite;
}

/* Particle sizes and positions */
.particle-1 { width: 4px; height: 4px; left: 5%; animation-duration: 25s; animation-delay: 0s; }
.particle-2 { width: 6px; height: 6px; left: 10%; animation-duration: 20s; animation-delay: -2s; }
.particle-3 { width: 3px; height: 3px; left: 15%; animation-duration: 28s; animation-delay: -4s; }
.particle-4 { width: 5px; height: 5px; left: 20%; animation-duration: 22s; animation-delay: -1s; }
.particle-5 { width: 4px; height: 4px; left: 25%; animation-duration: 26s; animation-delay: -3s; }
.particle-6 { width: 8px; height: 8px; left: 30%; animation-duration: 18s; animation-delay: -5s; }
.particle-7 { width: 10px; height: 10px; left: 35%; animation-duration: 24s; animation-delay: -7s; }
.particle-8 { width: 7px; height: 7px; left: 40%; animation-duration: 21s; animation-delay: -2s; }
.particle-9 { width: 9px; height: 9px; left: 45%; animation-duration: 19s; animation-delay: -8s; }
.particle-10 { width: 6px; height: 6px; left: 50%; animation-duration: 27s; animation-delay: -4s; }
.particle-11 { width: 4px; height: 4px; left: 55%; animation-duration: 23s; animation-delay: -6s; }
.particle-12 { width: 5px; height: 5px; left: 60%; animation-duration: 20s; animation-delay: -9s; }
.particle-13 { width: 3px; height: 3px; left: 65%; animation-duration: 29s; animation-delay: -1s; }
.particle-14 { width: 6px; height: 6px; left: 70%; animation-duration: 17s; animation-delay: -3s; }
.particle-15 { width: 4px; height: 4px; left: 75%; animation-duration: 25s; animation-delay: -5s; }
.particle-16 { width: 12px; height: 12px; left: 80%; animation-duration: 30s; animation-delay: -10s; }
.particle-17 { width: 14px; height: 14px; left: 85%; animation-duration: 22s; animation-delay: -12s; }
.particle-18 { width: 11px; height: 11px; left: 90%; animation-duration: 26s; animation-delay: -7s; }
.particle-19 { width: 8px; height: 8px; left: 95%; animation-duration: 19s; animation-delay: -4s; }
.particle-20 { width: 5px; height: 5px; left: 3%; animation-duration: 24s; animation-delay: -11s; }
.particle-21 { width: 3px; height: 3px; left: 12%; animation-duration: 31s; animation-delay: -6s; }
.particle-22 { width: 7px; height: 7px; left: 22%; animation-duration: 16s; animation-delay: -8s; }
.particle-23 { width: 4px; height: 4px; left: 33%; animation-duration: 28s; animation-delay: -13s; }
.particle-24 { width: 9px; height: 9px; left: 42%; animation-duration: 21s; animation-delay: -2s; }
.particle-25 { width: 5px; height: 5px; left: 52%; animation-duration: 24s; animation-delay: -9s; }
.particle-26 { width: 6px; height: 6px; left: 63%; animation-duration: 18s; animation-delay: -5s; }
.particle-27 { width: 8px; height: 8px; left: 72%; animation-duration: 27s; animation-delay: -14s; }
.particle-28 { width: 4px; height: 4px; left: 82%; animation-duration: 20s; animation-delay: -7s; }
.particle-29 { width: 10px; height: 10px; left: 88%; animation-duration: 23s; animation-delay: -3s; }
.particle-30 { width: 6px; height: 6px; left: 97%; animation-duration: 26s; animation-delay: -11s; }

/* Theme-specific particle colors */
.theme-default .particle-6,
.theme-default .particle-9,
.theme-default .particle-22,
.theme-default .particle-27 {
  background: rgba(89, 169, 106, 0.3);
}

.theme-default .particle-7,
.theme-default .particle-14,
.theme-default .particle-24,
.theme-default .particle-29 {
  background: rgba(60, 179, 113, 0.25);
}

.theme-default .particle-16,
.theme-default .particle-17 {
  background: rgba(46, 139, 87, 0.2);
}

.theme-default .particle-18 {
  background: rgba(144, 238, 144, 0.25);
}

.theme-medical .particle-6,
.theme-medical .particle-9,
.theme-medical .particle-22,
.theme-medical .particle-27 {
  background: rgba(59, 136, 165, 0.3);
}

.theme-medical .particle-7,
.theme-medical .particle-14,
.theme-medical .particle-24,
.theme-medical .particle-29 {
  background: rgba(70, 130, 180, 0.25);
}

.theme-medical .particle-16,
.theme-medical .particle-17 {
  background: rgba(30, 144, 255, 0.2);
}

.theme-medical .particle-18 {
  background: rgba(135, 206, 250, 0.25);
}

/* Theme Admin (Dark) */
.theme-admin .gradient-bg {
  background: linear-gradient(
    135deg,
    rgba(26, 31, 54, 1) 0%,
    rgba(15, 18, 37, 1) 30%,
    rgba(22, 27, 45, 1) 50%,
    rgba(18, 22, 40, 1) 70%,
    rgba(26, 31, 54, 1) 100%
  );
}

.theme-admin .particle {
  background: rgba(59, 136, 165, 0.4);
  box-shadow: 0 0 15px rgba(59, 136, 165, 0.3);
}

.theme-admin .particle-6,
.theme-admin .particle-9,
.theme-admin .particle-22,
.theme-admin .particle-27 {
  background: rgba(89, 169, 106, 0.4);
}

.theme-admin .particle-7,
.theme-admin .particle-14,
.theme-admin .particle-24,
.theme-admin .particle-29 {
  background: rgba(100, 150, 200, 0.35);
}

.theme-admin .particle-16,
.theme-admin .particle-17 {
  background: rgba(59, 136, 165, 0.5);
}

.theme-admin .particle-18 {
  background: rgba(120, 180, 220, 0.35);
}

@keyframes particleFloat {
  0% {
    transform: translateY(100vh) rotate(0deg);
    opacity: 0;
  }
  5% {
    opacity: 1;
  }
  95% {
    opacity: 1;
  }
  100% {
    transform: translateY(-20px) rotate(720deg);
    opacity: 0;
  }
}
</style>
